import { useState } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import { KeyRound, Search } from 'lucide-react';
import KioskPinPad from './KioskPinPad';
import KioskNameSearch from './KioskNameSearch';

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------
const TABS = [
  { id: 'pin', label: 'Enter PIN', icon: KeyRound },
  { id: 'search', label: 'Search Name', icon: Search },
];

// ---------------------------------------------------------------------------
// Content transition
// ---------------------------------------------------------------------------
const CONTENT_VARIANTS = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
};

const CONTENT_TRANSITION = {
  duration: 0.2,
  ease: [0.4, 0, 0.2, 1],
};

// ---------------------------------------------------------------------------
// KioskIdentifyScreen
// ---------------------------------------------------------------------------
// The identification screen with two tabs: PIN pad and name search.
//
// Props:
//   onVerifyPin      (function) - async, takes PIN string, returns { success, user, clockedIn, error }
//   employees        (array)    - full employee list for name search
//   onSelectUser     (function) - called with (user, clockedIn, method) when user identified
//   onBack           (function) - return to idle
//   onGetOnSiteUsers (function) - async, returns list of on-site users
// ---------------------------------------------------------------------------

export default function KioskIdentifyScreen({
  onVerifyPin,
  employees = [],
  selectUser,
  getOnSiteUsers,
  deviceInfo,
}) {
  // Read auth mode from device settings
  const settings = deviceInfo?.settings ?? {};
  const pinEnabled = settings.require_pin !== false;
  const searchEnabled = settings.allow_name_search !== false;

  // Determine which tabs to show
  const availableTabs = TABS.filter((tab) => {
    if (tab.id === 'pin') return pinEnabled;
    if (tab.id === 'search') return searchEnabled;
    return true;
  });

  const singleMode = availableTabs.length === 1;
  const defaultTab = availableTabs[0]?.id ?? 'pin';
  const [activeTab, setActiveTab] = useState(defaultTab);

  // -------------------------------------------------------------------------
  // When the PIN pad successfully verifies, forward to the parent.
  // -------------------------------------------------------------------------
  const handlePinSuccess = (user, clockedIn, method, onBreak, activeBreak) => {
    selectUser?.(user, clockedIn, method, onBreak, activeBreak);
  };

  // -------------------------------------------------------------------------
  // When a name-search row is selected, forward to the parent.
  // -------------------------------------------------------------------------
  const handleSearchSelect = (user, clockedIn, method, onBreak, activeBreak) => {
    selectUser?.(user, clockedIn, method, onBreak, activeBreak);
  };

  return (
    <div className="flex-1 flex flex-col px-6 py-6">
      {/* ----- Title ----- */}
      <h2 className="text-3xl font-bold text-slate-800 text-center mb-8">
        {singleMode && activeTab === 'pin'
          ? 'Enter your PIN'
          : singleMode && activeTab === 'search'
            ? 'Find your name'
            : 'How would you like to identify?'}
      </h2>

      {/* ----- Tabs (only when both modes enabled) ----- */}
      {!singleMode && (
        <div className="flex gap-3 justify-center mb-8">
          {availableTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <motion.button
                key={tab.id}
                type="button"
                className={
                  'flex items-center justify-center gap-2.5 h-14 px-8 rounded-xl text-lg font-semibold transition-colors select-none ' +
                  (isActive
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50')
                }
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveTab(tab.id)}
                aria-pressed={isActive}
                aria-label={tab.label}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
              </motion.button>
            );
          })}
        </div>
      )}

      {/* ----- Tab content ----- */}
      <div className="flex-1 flex flex-col items-center">
        <AnimatePresence mode="wait">
          {activeTab === 'pin' && pinEnabled && (
            <motion.div
              key="pin"
              variants={CONTENT_VARIANTS}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={CONTENT_TRANSITION}
              className="w-full flex justify-center"
            >
              <KioskPinPad
                onVerifyPin={onVerifyPin}
                onSuccess={handlePinSuccess}
              />
            </motion.div>
          )}

          {activeTab === 'search' && searchEnabled && (
            <motion.div
              key="search"
              variants={CONTENT_VARIANTS}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={CONTENT_TRANSITION}
              className="w-full flex justify-center"
            >
              <KioskNameSearch
                employees={employees}
                onSelect={handleSearchSelect}
                onGetOnSiteUsers={getOnSiteUsers}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
